import React from 'react';
import { Card } from "@/components/ui/card";
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import { ArrowRight, TrendingUp, Target, Lightbulb, Zap } from "lucide-react";
import { useNavigate } from 'react-router-dom';

interface CrossToolInsight {
  id: string;
  type: 'connection' | 'recommendation' | 'opportunity' | 'synergy';
  title: string;
  description: string;
  affected_tools: string[];
  confidence_score: number;
  action_url?: string;
  action_text?: string;
}

interface CareerArtifactIntelligenceProps {
  userScore?: number;
  completedArtifacts: string[];
  recentActivity: string[];
}

export const CareerArtifactIntelligence = ({ 
  userScore = 0, 
  completedArtifacts = [], 
  recentActivity = [] 
}: CareerArtifactIntelligenceProps) => {
  const navigate = useNavigate();

  // Generate insights based on user data
  const generateInsights = (): CrossToolInsight[] => {
    const insights: CrossToolInsight[] = [];

    // Big Sheet → Power Moves connection
    if (completedArtifacts.includes('big-sheet') && !completedArtifacts.includes('power-moves')) {
      insights.push({
        id: 'bigsheet-powermoves',
        type: 'connection',
        title: 'Transform Big Sheet Insights into Action',
        description: 'Your completed Big Sheet reveals key growth areas. Create Power Moves to act on these insights.',
        affected_tools: ['big-sheet', 'power-moves'],
        confidence_score: 85,
        action_url: '/artifacts/power-moves',
        action_text: 'Create Power Moves'
      });
    }

    // Score-based recommendations
    if (userScore > 0 && userScore < 60) {
      insights.push({
        id: 'score-improvement',
        type: 'recommendation',
        title: 'Focus on Skill Development',
        description: 'Your Loaded Score suggests focusing on technical skills. Set specific learning OKRs.',
        affected_tools: ['assessment', 'okrs'],
        confidence_score: 90,
        action_url: '/artifacts/okrs',
        action_text: 'Create Learning OKRs'
      });
    }

    // OKR → Opportunity Audit synergy
    if (completedArtifacts.includes('okrs') && !completedArtifacts.includes('opportunity-audit')) {
      insights.push({
        id: 'okr-opportunities',
        type: 'synergy',
        title: 'Align Goals with Opportunities',
        description: 'Your OKRs are set. Now audit opportunities that accelerate these goals.',
        affected_tools: ['okrs', 'opportunity-audit'],
        confidence_score: 80,
        action_url: '/artifacts/opportunity-audit',
        action_text: 'Audit Opportunities'
      });
    }

    // Cross-artifact completion opportunity
    if (completedArtifacts.length >= 2) {
      insights.push({
        id: 'ecosystem-synergy',
        type: 'opportunity',
        title: 'Unlock Ecosystem Benefits',
        description: 'You\'re building momentum! Complete all 4 artifacts to unlock advanced career intelligence.',
        affected_tools: ['big-sheet', 'power-moves', 'okrs', 'opportunity-audit'],
        confidence_score: 95,
        action_text: 'View All Artifacts'
      });
    }

    return insights;
  };

  const insights = generateInsights();

  const getIcon = (type: string) => {
    switch (type) {
      case 'connection': return TrendingUp;
      case 'recommendation': return Lightbulb;
      case 'opportunity': return Target;
      case 'synergy': return Zap;
      default: return TrendingUp;
    }
  };

  const getTypeColor = (type: string) => {
    switch (type) {
      case 'connection': return 'bg-blue-500/10 text-blue-500';
      case 'recommendation': return 'bg-amber-500/10 text-amber-500';
      case 'opportunity': return 'bg-green-500/10 text-green-500';
      case 'synergy': return 'bg-purple-500/10 text-purple-500';
      default: return 'bg-primary/10 text-primary';
    }
  };

  if (insights.length === 0) {
    return null;
  }

  return (
    <Card className="bg-gradient-card border-border/10 p-6">
      <div className="flex items-center gap-3 mb-4">
        <div className="p-2 rounded-lg bg-primary/10">
          <TrendingUp className="h-5 w-5 text-primary" />
        </div>
        <div>
          <h3 className="text-lg font-semibold">Career Intelligence</h3>
          <p className="text-sm text-muted-foreground">
            Smart connections across your artifacts
          </p>
        </div>
      </div>

      <div className="space-y-4">
        {insights.map((insight) => {
          const Icon = getIcon(insight.type);
          
          return (
            <div key={insight.id} className="border border-border/20 rounded-lg p-4 space-y-3">
              <div className="flex items-start justify-between gap-3">
                <div className="flex items-start gap-3 flex-1">
                  <Icon className="h-4 w-4 text-primary mt-0.5 flex-shrink-0" />
                  <div className="flex-1 min-w-0">
                    <div className="flex items-center gap-2 mb-1">
                      <h4 className="font-medium text-sm">{insight.title}</h4>
                      <Badge 
                        variant="secondary" 
                        className={`text-xs ${getTypeColor(insight.type)}`}
                      >
                        {insight.type}
                      </Badge>
                    </div>
                    <p className="text-sm text-muted-foreground">{insight.description}</p>
                  </div>
                </div>
                <div className="text-xs text-muted-foreground">
                  {insight.confidence_score}%
                </div>
              </div>

              <div className="flex items-center justify-between">
                <div className="flex gap-1">
                  {insight.affected_tools.map((tool) => (
                    <Badge key={tool} variant="outline" className="text-xs">
                      {tool.replace('-', ' ')}
                    </Badge>
                  ))}
                </div>
                {insight.action_url && insight.action_text && (
                  <Button 
                    size="sm" 
                    variant="ghost"
                    onClick={() => navigate(insight.action_url!)}
                    className="text-xs h-auto py-1 px-2"
                  >
                    {insight.action_text}
                    <ArrowRight className="h-3 w-3 ml-1" />
                  </Button>
                )}
              </div>
            </div>
          );
        })}
      </div>
    </Card>
  );
};